import { ReactNode, useCallback, useState } from "react";
import {
  ErrorDialogContext,
  ErrorDialogContextType,
  useErrorContext,
} from "@/shared/context/error-context";
import { ErrorPage } from "@/shared/components/global-error-handling/error-page";
import { ErrorDialog } from "@/shared/components/global-error-handling/error-dialog";

// ============================================================================
// ErrorRenderer コンポーネント
// ============================================================================

/**
 * エラー表示タイプに応じてUIを切り替えるコンポーネント
 * 
 * @description
 * - ErrorPage: 致命的エラー時に表示（children は非表示）
 * - ErrorDialog: Portal を使用して document.body に直接レンダリングされるため、
 *   children の状態（モーダル等）に影響を与えずにエラーダイアログを表示できる
 */
const ErrorRenderer = ({ children }: { children: ReactNode }) => {
  const { displayType } = useErrorContext();

  // エラーページ表示の場合は children を表示せず、ErrorPage のみ表示
  if (displayType === "page") {
    return <ErrorPage />;
  }

  // それ以外の場合は children のみ表示
  // ErrorDialog は Portal を使用するため、ここでレンダリングする必要はないが、
  // コンポーネントとしてマウントしておく必要がある
  return (
    <>
      {children}
      <ErrorDialog />
    </>
  );
};

// ============================================================================
// ErrorDialogProvider コンポーネント
// ============================================================================

/**
 * グローバルエラーハンドリングの Provider
 *
 * @description
 * ErrorDialogContext を提供し、アプリ全体でエラー状態を共有する。
 * login-context と同様の構造で、context フォルダ内に Provider を配置。
 *
 * @example
 * <ErrorDialogProvider>
 *   <App />
 * </ErrorDialogProvider>
 */
export const ErrorDialogProvider = ({ children }: { children: ReactNode }) => {
  const [error, _setError] = useState<Error | undefined>(undefined);

  const setError = useCallback((error: Error | undefined): void => {
    _setError(error);
  }, []);

  const contextValue: ErrorDialogContextType = {
    error,
    setError,
  };

  return (
    <ErrorDialogContext.Provider value={contextValue}>
      <ErrorRenderer>{children}</ErrorRenderer>
    </ErrorDialogContext.Provider>
  );
};
